import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import InviteEmailDialog from '../components/InviteEmailDialog';
import logger from '../lib/logger';
import {
  Building2, Users, Shield, Clock, BarChart3, FileText, Settings, AlertCircle,
  Plus, Pencil, Trash2, Search, Loader2, Upload, Eye, ChevronDown,
  ChevronUp, Calendar, DollarSign, Download, X, Image, UserPlus, Key, CalendarDays, Briefcase,
  ScrollText, Check, AlertTriangle, Mail, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import TemplateAnalyticsTab from '../components/admin/TemplateAnalyticsTab';
import DisplayNamesTab from '../components/admin/DisplayNamesTab';
import { OrgBackfillSection } from '../components/admin/OrgBackfillSection';
import { LeadIdentityMergeCard } from '../components/admin/LeadIdentityMergeCard';
import { SessionSecurityAdminCard } from '../components/admin/SessionSecurityAdminCard';
import { LogOut as LogOutIcon } from 'lucide-react';
import FirmStyleGuardrailsTab from '../components/admin/FirmStyleGuardrailsTab';
import SOWHealthTab from '../components/admin/SOWHealthTab';
import AllSOWsTab from '../components/admin/AllSOWsTab';
import SeertechEntitiesTab from '../components/admin/SeertechEntitiesTab';
import ClientErrorsTab from '../components/admin/ClientErrorsTab';
import CurrenciesTab from '../components/admin/CurrenciesTab';
import OrgFormInline from '../components/admin/OrgFormInline';
import UserFormInline from '../components/admin/UserFormInline';
import BuildVersionFooter from '../components/admin/BuildVersionFooter';
import AuditLogTab from '../components/admin/AuditLogTab';
import { QuoteGateAcksCard } from '../components/admin/QuoteGateAcksCard';
import AutoArchiveSettingsCard from '../components/admin/AutoArchiveSettingsCard';
import RiskSettingsCard from '../components/admin/RiskSettingsCard';
import FxRatesCard from '../components/admin/FxRatesCard';
import PriceBookCard from '../components/admin/PriceBookCard';
import { GoogleDriveSettingsCard } from '../components/admin/GoogleDriveSettingsCard';
import { SupportServicesCard } from '../components/admin/SupportServicesCard';
import AddonCatalogueCard from '../components/admin/AddonCatalogueCard';
import { CacModelCard } from '../components/admin/CacModelCard';
import { CacCalibrationCard } from '../components/admin/CacCalibrationCard';
import { CacModelHistoryCard } from '../components/admin/CacModelHistoryCard';
import { OverrideReasonsCard } from '../components/admin/OverrideReasonsCard';
import { RegionalTargetsCard } from '../components/admin/RegionalTargetsCard';
import RegionalRateCardsCard from '../components/admin/RegionalRateCardsCard';
import CostRateCardCard from '../components/admin/CostRateCardCard';
import { ROLE_BADGES, formatDuration } from '../components/admin/_helpers';
import { TreatmentMemoryPanel } from '../components/admin/TreatmentMemoryPanel';
import { PortalMappingsPanel } from '../components/admin/PortalMappingsPanel';
import { NarrativeLibraryPanel } from '../components/admin/NarrativeLibraryPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// v378 — collapsible Settings sections (Risk Badges, Pricing, …).
function SettingsSection({ title, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="text-sm font-semibold text-[#0F2040]">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

const ADMIN_TABS = [
  { id: 'organizations', label: 'Organizations', icon: Building2 },
  { id: 'org-backfill', label: 'Org Backfill', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'audit-log', label: 'Audit Log', icon: ScrollText, superAdminOnly: true },
  { id: 'template-analytics', label: 'Template Analytics', icon: BarChart3, superAdminOnly: true },
  { id: 'display-names', label: 'Display Names', icon: FileText, superAdminOnly: true },
  { id: 'firm-guardrails', label: 'AI Guardrails', icon: Shield, superAdminOnly: true },
  { id: 'sow-health', label: 'SOW Health', icon: FileText, superAdminOnly: true },
  { id: 'all-sows', label: 'All SOWs', icon: FileText, superAdminOnly: true },
  { id: 'seertech-entities', label: 'Seertech Entities', icon: Building2, superAdminOnly: true },
  { id: 'currencies', label: 'Currencies', icon: DollarSign, superAdminOnly: true },
  { id: 'price-book', label: 'Price Book & CAC', icon: DollarSign },
  { id: 'rfp-memory', label: 'RFP Memory', icon: FileText },
  { id: 'client-errors', label: 'Client Errors', icon: AlertCircle, superAdminOnly: true },
  { id: 'permissions', label: 'Permissions', icon: Shield },
  { id: 'estimates', label: 'Estimate History', icon: Clock },
  { id: 'dashboards', label: 'Dashboards', icon: BarChart3 },
  { id: 'reports', label: 'Reports', icon: FileText },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// Sidebar UX pass (Feb 2026): collapse the 15 tabs into three semantic
// groups so the rail reads as scannable sections instead of one long
// alphabet soup. The existing tab IDs are unchanged — just visually
// grouped in the rail.
const ADMIN_GROUPS = [
  {
    id: 'tenant',
    label: 'Tenant',
    tabIds: ['organizations', 'org-backfill', 'users', 'permissions', 'audit-log', 'seertech-entities'],
  },
  {
    id: 'operations',
    label: 'Operations',
    tabIds: ['estimates', 'dashboards', 'reports', 'sow-health', 'all-sows', 'client-errors'],
  },
  {
    id: 'configuration',
    label: 'Configuration',
    tabIds: ['settings', 'price-book', 'rfp-memory', 'currencies', 'display-names', 'template-analytics', 'firm-guardrails'],
  },
];

const PERMISSION_MATRIX = {
  super_admin: {
    label: 'Super Admin',
    color: 'purple',
    permissions: [
      'Full system access',
      'Create, edit, delete organizations',
      'Create, edit, delete users and assign all roles',
      'Create, edit, delete all estimates',
      'Edit master rate cards, components, and volume metrics',
      'View all dashboards and reports',
      'Manage system settings and SOW template',
      'Approve or reject SOWs',
      'Can action all approval requests',
    ]
  },
  cco: {
    label: 'CCO (Chief Customer Officer)',
    color: 'rose',
    permissions: [
      'View all estimates for all organizations',
      'Create and edit own estimates',
      'Create, edit, delete organizations and users',
      'Approve or reject SOWs before client delivery',
      'Update SOW template',
      'Manage system settings',
    ]
  },
  cro: {
    label: 'CRO (Sales Leader)',
    color: 'amber',
    permissions: [
      'View all estimates for all organizations',
      'Create and edit own estimates',
      'Create organizations and users',
      'Update SOW template',
      'Informal quote approval authority',
    ]
  },
  admin: {
    label: 'Sales',
    color: 'blue',
    permissions: [
      'Create and edit organizations',
      'Create and edit users (user, admin, CS roles)',
      'Create and edit their own estimates for any organization',
      'Submit approval requests and track them',
      'Generate SOW from estimate',
      'Cannot approve SOWs or quotes',
    ]
  },
  cs: {
    label: 'CS (Customer Success)',
    color: 'teal',
    permissions: [
      'Create and edit organizations',
      'Create and edit users (user, admin, CS roles)',
      'Create and edit their own estimates for any organization',
      'Submit approval requests and track them',
      'Generate SOW from estimate',
      'Cannot approve SOWs or quotes',
    ]
  },
  user: {
    label: 'User',
    color: 'slate',
    permissions: [
      'View estimates for their organization only',
      'Generate and download output reports (PDF, Word, Excel, PPTX)',
      'Cannot create or edit estimates',
      'Cannot access admin settings or user management',
      'Can view their own profile and communication preferences',
    ]
  },
};

export default function AdminPage() {
  const { user, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'organizations');
  const [cacModelKey, setCacModelKey] = useState(0);
  const [cacHistoryKey, setCacHistoryKey] = useState(0);
  const [currencySubTab, setCurrencySubTab] = useState('list');
  const isAdmin = ['admin', 'cs', 'cro', 'cco', 'super_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isOwner = !!user?.is_owner;
  const canAssignAllRoles = ['super_admin', 'cco'].includes(user?.role);

  // Shared state
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Organizations state
  const [orgs, setOrgs] = useState([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [savingOrg, setSavingOrg] = useState(false);
  const [lookups, setLookups] = useState({ industries: [], countries: [], lms_types: [] });

  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [orgForNewUser, setOrgForNewUser] = useState(null);
  const [inviteData, setInviteData] = useState(null); // {email, name, role, org_name}
  const [userToDelete, setUserToDelete] = useState(null); // {email, name, estimateCount}
  const [pendingDemote, setPendingDemote] = useState(null); // userData pending super-admin role-change confirm
  const [saDeleteConfirmed, setSaDeleteConfirmed] = useState(false); // checkbox gate for super_admin deletes
  const [deletingUser, setDeletingUser] = useState(false);

  // Estimates state
  const [estimates, setEstimates] = useState([]);
  const [estSearch, setEstSearch] = useState('');
  const [estPage, setEstPage] = useState(1);
  const [estTotal, setEstTotal] = useState(0);
  const [orgPage, setOrgPage] = useState(1);
  const [orgTotal, setOrgTotal] = useState(0);
  const ADMIN_PAGE_SIZE = 30;

  // (Audit-log state extracted to <AuditLogTab /> in v221.)
  // Per-user audit history still lives here because it powers the
  // inline expander on the Users tab (not the dedicated Audit Log tab).
  const [userAuditHistory, setUserAuditHistory] = useState({});
  const [expandedUserAudit, setExpandedUserAudit] = useState(null);

  useEffect(() => {
    if (!isAdmin) { navigate('/estimator'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const promises = [
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/organizations`, { params: { page: orgPage, limit: ADMIN_PAGE_SIZE }, withCredentials: true }),
        axios.get(`${API_URL}/api/organizations/lookups`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/estimates`, { params: { page: estPage, limit: ADMIN_PAGE_SIZE }, withCredentials: true }),
      ];
      const [statsRes, orgsRes, lookupsRes, usersRes, estimatesRes] = await Promise.all(promises);
      setStats(statsRes.data);
      setOrgs(orgsRes.data.organizations || []);
      setOrgTotal(orgsRes.data.total || 0);
      setLookups(lookupsRes.data);
      setUsers(usersRes.data.users || []);
      setEstimates(estimatesRes.data.estimates || []);
      setEstTotal(estimatesRes.data.total || 0);
    } catch (err) {
      logger.error('Failed to fetch admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgs = async (p) => {
    try {
      const res = await axios.get(`${API_URL}/api/organizations`, { params: { page: p, limit: ADMIN_PAGE_SIZE, search: orgSearch }, withCredentials: true });
      setOrgs(res.data.organizations || []);
      setOrgTotal(res.data.total || 0);
    } catch (err) { logger.error(err); }
  };

  const fetchEstimates = async (p) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/estimates`, { params: { page: p, limit: ADMIN_PAGE_SIZE, search: estSearch }, withCredentials: true });
      setEstimates(res.data.estimates || []);
      setEstTotal(res.data.total || 0);
    } catch (err) { logger.error(err); }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // ── AUDIT LOG HANDLERS ────────────────────────────────────
  // (Top-level Audit Log tab is now <AuditLogTab />. The per-user
  // history expander on the Users tab continues to live here.)
  const fetchUserAuditHistory = async (email) => {
    if (expandedUserAudit === email) {
      setExpandedUserAudit(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/admin/impersonate/audit-log/user/${email}`, { withCredentials: true });
      setUserAuditHistory(prev => ({ ...prev, [email]: res.data.entries || [] }));
      setExpandedUserAudit(email);
    } catch (err) { logger.error(err); }
  };

  // ── ORG HANDLERS ──────────────────────────────────────────
  const saveOrg = async (formData) => {
    setSavingOrg(true);
    try {
      if (editingOrg) {
        await axios.put(`${API_URL}/api/organizations/${editingOrg.org_id}`, formData, { withCredentials: true });
        toast.success('Organization updated');
      } else {
        await axios.post(`${API_URL}/api/organizations`, formData, { withCredentials: true });
        toast.success('Organization created');
      }
      setShowOrgForm(false); setEditingOrg(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save organization');
    } finally { setSavingOrg(false); }
  };

  const deleteOrg = async (orgId) => {
    if (!window.confirm('Delete this organization?')) return;
    try {
      await axios.delete(`${API_URL}/api/organizations/${orgId}`, { withCredentials: true });
      toast.success('Organization deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  // ── USER HANDLERS ─────────────────────────────────────────
  const saveUser = async (userData, confirmed = false) => {
    // Guard: changing a Super Admin's role to anything lower is a
    // high-impact action — require an explicit confirmation modal first.
    if (
      !confirmed &&
      editingUser?.role === 'super_admin' &&
      userData.role &&
      userData.role !== 'super_admin'
    ) {
      setPendingDemote(userData);
      return;
    }
    setSavingUser(true);
    try {
      if (editingUser) {
        const putRes = await axios.put(`${API_URL}/api/admin/users/${editingUser.email}`, userData, { withCredentials: true });
        // Use the canonical `user` payload returned by the backend
        // (falls back to the form's submitted data) so the visible
        // table immediately reflects the saved state — the badge can
        // never appear to "revert" even if a subsequent `fetchAll` is
        // slow or briefly returns stale data on the path through any
        // intermediate cache.
        const refreshed = putRes?.data?.user || { ...editingUser, ...userData };
        setUsers(prev => prev.map(u => u.email === editingUser.email ? { ...u, ...refreshed } : u));
        toast.success('User updated');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/users`, userData, { withCredentials: true });
        toast.success(res.data.message);
        // Resolve org name for invite
        let orgDisplayName = '';
        if (orgForNewUser) {
          orgDisplayName = orgForNewUser.name;
        } else if (userData.org_id) {
          const org = orgs.find(o => o.org_id === userData.org_id);
          if (org) orgDisplayName = org.name;
        }
        // Open invite email dialog
        setInviteData({
          email: userData.email,
          name: userData.name,
          role: userData.role,
          org_name: orgDisplayName,
        });
      }
      setShowUserForm(false); setEditingUser(null); setOrgForNewUser(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally { setSavingUser(false); }
  };

  const handleForceSignOut = async (u) => {
    if (!window.confirm(`Sign ${u.name || u.email} out of every device? They will need to sign in again.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/users/${encodeURIComponent(u.email)}/sign-out-everywhere`, {}, { withCredentials: true });
      toast.success(`${u.name || u.email} signed out of ${r.data.removed} device${r.data.removed === 1 ? '' : 's'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not sign the user out'); }
  };

  const handleImpersonate = async (email) => {
    try {
      await impersonateUser(email);
      toast.success(`Now viewing as ${email}`);
      navigate('/history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);
    try {
      const res = await axios.delete(`${API_URL}/api/admin/users/${userToDelete.email}`, { withCredentials: true });
      const { estimates_reassigned, sows_reassigned } = res.data;
      let msg = `User ${userToDelete.email} deleted`;
      if (estimates_reassigned > 0 || sows_reassigned > 0) {
        msg += ` — ${estimates_reassigned} estimate(s) reassigned to you`;
      }
      toast.success(msg);
      setUserToDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const filteredOrgs = useMemo(() => orgs.filter(o =>
    o.name?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    o.industry?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    o.country?.toLowerCase().includes(orgSearch.toLowerCase())
  ), [orgs, orgSearch]);

  const filteredUsers = useMemo(() => users.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.role?.toLowerCase().includes(userSearch.toLowerCase())
  ), [users, userSearch]);

  const filteredEstimates = useMemo(() => estimates.filter(e =>
    e.estimate_name?.toLowerCase().includes(estSearch.toLowerCase()) ||
    e.user_name?.toLowerCase().includes(estSearch.toLowerCase()) ||
    e.user_email?.toLowerCase().includes(estSearch.toLowerCase())
  ), [estimates, estSearch]);

  const estimatesByUserEmail = useMemo(() => {
    const map = {};
    for (const e of estimates) {
      const email = e.user_email || '';
      if (!map[email]) map[email] = [];
      map[email].push(e);
    }
    return map;
  }, [estimates]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Organizations', value: stats?.total_orgs || 0, icon: Building2, color: 'text-[#4ABFCF]' },
          { label: 'Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-500' },
          { label: 'Estimates', value: stats?.total_estimates || 0, icon: Clock, color: 'text-amber-500' },
          { label: 'Admins', value: stats?.admin_count || 0, icon: Shield, color: 'text-purple-500' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-8 h-8 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold text-[#0F2040]">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-6">
        {/* Sidebar — grouped accordions (Tenant · Operations · Config) */}
        <nav className="w-52 shrink-0 space-y-3" data-testid="admin-sidebar">
          {ADMIN_GROUPS.map(group => {
            const tabs = group.tabIds
              .map(id => ADMIN_TABS.find(t => t.id === id))
              .filter(t => t && (!t.superAdminOnly || isSuperAdmin));
            if (!tabs.length) return null;
            return (
              <div key={group.id} data-testid={`admin-sidebar-group-${group.id}`}>
                <div className="px-3 mb-1 text-[9px] font-bold tracking-wider uppercase text-slate-400">
                  {group.label}
                </div>
                <div className="space-y-0.5">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        activeTab === tab.id ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`admin-tab-${tab.id}`}
                    >
                      <tab.icon className="w-3.5 h-3.5" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">

          {activeTab === 'org-backfill' && <OrgBackfillSection />}

          {/* ═══════════ ORGANIZATIONS ═══════════════════════════ */}
          {activeTab === 'organizations' && (
            <div className="space-y-4" data-testid="admin-organizations">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[#0F2040]">Organizations</h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={orgSearch} onChange={e => setOrgSearch(e.target.value)} placeholder="Search orgs..." className="pl-8 text-xs h-8 w-48" data-testid="org-search" />
                  </div>
                  {isAdmin && (
                    <Button size="sm" onClick={() => { setEditingOrg(null); setShowOrgForm(true); }} className="bg-[#4ABFCF] hover:bg-[#3AAAB9]" data-testid="create-org-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> New Org
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {filteredOrgs.map(org => (
                  <Card key={org.org_id} data-testid={`org-card-${org.org_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: org.primary_color || '#0F2040' }}>
                            {org.name?.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm text-[#0F2040]">{org.name}</h3>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {org.industry && <Badge variant="outline" className="text-[10px]">{org.industry}</Badge>}
                              {org.country && <span>{org.country}</span>}
                              {org.contact_email && <span>{org.contact_email}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {org.lms_types && (
                            <div className="flex gap-1">
                              {org.lms_types.split(',').filter(Boolean).slice(0, 2).map((t) => (
                                <Badge key={t.trim()} variant="secondary" className="text-[10px]">{t.trim()}</Badge>
                              ))}
                            </div>
                          )}
                          {isAdmin && (
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" className="h-7 px-2 gap-1 text-[#4ABFCF] hover:text-[#3AAAB9] hover:bg-[#4ABFCF]/5 border-[#4ABFCF]/30" onClick={(e) => { e.stopPropagation(); setEditingUser(null); setOrgForNewUser(org); setShowUserForm(true); }} data-testid={`add-user-org-${org.org_id}`} title={`Add user to ${org.name}`}>
                                <UserPlus className="w-3 h-3" />
                                <span className="text-[10px]">Add User</span>
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingOrg(org); setShowOrgForm(true); }} data-testid={`edit-org-${org.org_id}`}><Pencil className="w-3 h-3" /></Button>
                              {isSuperAdmin && <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => deleteOrg(org.org_id)} data-testid={`delete-org-${org.org_id}`}><Trash2 className="w-3 h-3" /></Button>}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {filteredOrgs.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No organizations found.</p>}
                {/* Org Pagination */}
                {Math.ceil(orgTotal / ADMIN_PAGE_SIZE) > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-4" data-testid="org-pagination">
                    <Button variant="outline" size="sm" disabled={orgPage <= 1} onClick={() => { const p = orgPage - 1; setOrgPage(p); fetchOrgs(p); }} data-testid="org-page-prev">Prev</Button>
                    <span className="text-xs text-slate-500">Page {orgPage} of {Math.ceil(orgTotal / ADMIN_PAGE_SIZE)}</span>
                    <Button variant="outline" size="sm" disabled={orgPage >= Math.ceil(orgTotal / ADMIN_PAGE_SIZE)} onClick={() => { const p = orgPage + 1; setOrgPage(p); fetchOrgs(p); }} data-testid="org-page-next">Next</Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══════════ USERS ══════════════════════════════════ */}
          {activeTab === 'users' && (
            <div className="space-y-4" data-testid="admin-users">
              <LeadIdentityMergeCard />
              <SessionSecurityAdminCard canEdit={isSuperAdmin} />
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[#0F2040]">Users</h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..." className="pl-8 text-xs h-8 w-48" data-testid="user-search" />
                  </div>
                  {isAdmin && (
                    <Button size="sm" onClick={() => { setEditingUser(null); setOrgForNewUser(null); setShowUserForm(true); }} className="bg-[#4ABFCF] hover:bg-[#3AAAB9]" data-testid="create-user-btn">
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> New User
                    </Button>
                  )}
                </div>
              </div>

              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500 w-[14%]">Name</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500 w-[24%]">Email</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500 w-[14%]">Organization</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500 w-[10%]">Role</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500 w-[8%]">Estimates</th>
                        {isAdmin && <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 w-[30%]">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u => {
                        const userEstimates = estimatesByUserEmail[u.email] || [];
                        const orgName = orgs.find(o => o.org_id === u.org_id)?.name;
                        const roleBadge = ROLE_BADGES[u.role] || ROLE_BADGES.user;
                        return (
                          <React.Fragment key={u.email}>
                            <tr className="border-b hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setExpandedUser(expandedUser === u.email ? null : u.email)}>
                              <td className="px-3 py-2.5 font-medium text-[#0F2040] truncate">
                                <span className="inline-flex items-center gap-1.5">
                                  {u.name}
                                  {u.is_owner && (
                                    <Badge className="text-[9px] bg-amber-100 text-amber-800 border border-amber-300 gap-0.5 px-1.5 py-0" data-testid={`owner-badge-${u.email}`} title="Owner account — full control over all accounts">
                                      <Crown className="w-2.5 h-2.5" /> Owner
                                    </Badge>
                                  )}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-slate-600 truncate" title={u.email}>{u.email}</td>
                              <td className="px-3 py-2.5 text-slate-500 truncate">{orgName || '—'}</td>
                              <td className="px-3 py-2.5"><Badge className={`text-[10px] ${roleBadge.className}`}>{roleBadge.label}</Badge></td>
                              <td className="px-3 py-2.5 text-slate-500">{userEstimates.length}</td>
                              {isAdmin && (
                                <td className="px-3 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-1 flex-nowrap">
                                    {u.role !== 'super_admin' && isSuperAdmin && (
                                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200" onClick={(e) => { e.stopPropagation(); handleImpersonate(u.email); }} data-testid={`impersonate-user-${u.email}`} title={`View as ${u.name}`}>
                                        <Eye className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {isSuperAdmin && (
                                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" onClick={(e) => { e.stopPropagation(); handleForceSignOut(u); }} data-testid={`force-signout-user-${u.email}`} title={`Sign ${u.name} out of every device`}>
                                        <LogOutIcon className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {u.role !== 'super_admin' && (
                                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 border-cyan-200" onClick={(e) => { e.stopPropagation(); setInviteData({ email: u.email, name: u.name, role: u.role, org_name: orgName || '' }); }} data-testid={`resend-invite-${u.email}`} title={`Resend invite email to ${u.name}`}>
                                        <Mail className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {(u.role !== 'super_admin' || isOwner) && (
                                      <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); setEditingUser(u); setOrgForNewUser(null); setShowUserForm(true); }} data-testid={`edit-user-${u.email}`}>
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {((u.role !== 'super_admin' && isSuperAdmin) || (u.role === 'super_admin' && isOwner)) && (
                                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200" onClick={(e) => { e.stopPropagation(); setUserToDelete({ email: u.email, name: u.name, estimateCount: userEstimates.length, role: u.role }); }} data-testid={`delete-user-${u.email}`} title="Delete user">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                            {expandedUser === u.email && (
                              <tr>
                                <td colSpan={isAdmin ? 6 : 5} className="bg-slate-50 px-6 py-3 space-y-3">
                                  {userEstimates.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-slate-500 mb-2">Estimate History ({userEstimates.length})</p>
                                      <div className="space-y-1">
                                        {userEstimates.slice(0, 5).map((est) => (
                                          <div key={est.id || est.estimate_name} className="flex items-center justify-between text-xs bg-white rounded px-3 py-1.5 border border-slate-100">
                                            <span className="font-medium text-[#0F2040]">{est.estimate_name}</span>
                                            <div className="flex items-center gap-3 text-slate-500">
                                              <span>{est.implementation_type?.replace(/_/g, ' ')}</span>
                                              <span>{est.results?.total_effort?.toLocaleString() || 0} hrs</span>
                                              <span>${est.results?.total_cost?.toLocaleString() || 0}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {isSuperAdmin && (
                                    <div>
                                      <button onClick={(e) => { e.stopPropagation(); fetchUserAuditHistory(u.email); }} className="text-xs font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1" data-testid={`user-audit-toggle-${u.email}`}>
                                        <Eye className="w-3 h-3" />
                                        {expandedUserAudit === u.email ? 'Hide' : 'Show'} Impersonation History
                                      </button>
                                      {expandedUserAudit === u.email && (
                                        <div className="mt-2 space-y-1">
                                          {(userAuditHistory[u.email] || []).length === 0 ? (
                                            <p className="text-xs text-slate-400 italic">No impersonation history</p>
                                          ) : (
                                            (userAuditHistory[u.email] || []).map((a, ai) => (
                                              <div key={a.audit_id || ai} className="flex items-center justify-between text-xs bg-amber-50 rounded px-3 py-1.5 border border-amber-100">
                                                <div className="flex items-center gap-2">
                                                  <Eye className="w-3 h-3 text-amber-500" />
                                                  <span className="text-[#0F2040]">by <strong>{a.admin_name}</strong></span>
                                                </div>
                                                <div className="flex items-center gap-3 text-slate-500">
                                                  <span>{a.started_at ? new Date(a.started_at).toLocaleDateString() : ''}</span>
                                                  <span>{formatDuration(a.duration_seconds)}</span>
                                                  <span>{(a.pages_visited || []).length} pages</span>
                                                </div>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No users found.</p>}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'template-analytics' && isSuperAdmin && (
            <TemplateAnalyticsTab />
          )}

          {activeTab === 'display-names' && isSuperAdmin && (
            <DisplayNamesTab />
          )}

          {activeTab === 'firm-guardrails' && isSuperAdmin && (
            <FirmStyleGuardrailsTab />
          )}

          {activeTab === 'sow-health' && isSuperAdmin && (
            <SOWHealthTab />
          )}

          {activeTab === 'all-sows' && isSuperAdmin && (
            <AllSOWsTab />
          )}

          {activeTab === 'seertech-entities' && isSuperAdmin && (
            <SeertechEntitiesTab />
          )}

          {activeTab === 'rfp-memory' && (<div className="space-y-6"><NarrativeLibraryPanel /><TreatmentMemoryPanel /><PortalMappingsPanel /></div>)}

          {activeTab === 'price-book' && (
            <div className="space-y-6">
              <div data-testid="admin-price-book"><PriceBookCard /></div>
              <div data-testid="admin-addon-catalogue"><AddonCatalogueCard /></div>
              <div data-testid="admin-support-services"><SupportServicesCard /></div>
              <div data-testid="admin-override-reasons"><OverrideReasonsCard /></div>
              <div data-testid="admin-regional-targets"><RegionalTargetsCard /></div>
              <div data-testid="admin-cac-model"><CacModelCard key={cacModelKey} onSaved={() => setCacHistoryKey((k) => k + 1)} /></div>
              <div data-testid="admin-cac-calibration"><CacCalibrationCard onApplied={() => { setCacModelKey((k) => k + 1); setCacHistoryKey((k) => k + 1); }} /></div>
              <div data-testid="admin-cac-history"><CacModelHistoryCard refreshKey={cacHistoryKey} onReverted={() => { setCacModelKey((k) => k + 1); setCacHistoryKey((k) => k + 1); }} /></div>
            </div>
          )}

          {activeTab === 'currencies' && isSuperAdmin && (
            <div className="space-y-4" data-testid="admin-currencies">
              <div className="flex items-center gap-1 border-b border-slate-200" data-testid="currencies-subtabs">
                {[{ id: 'list', label: 'Currencies' }, { id: 'fx', label: 'Conversion & Rates (FX)' }].map(t => (
                  <button key={t.id} onClick={() => setCurrencySubTab(t.id)}
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                      currencySubTab === t.id ? 'border-[#0F2040] text-[#0F2040]' : 'border-transparent text-slate-500 hover:text-[#0F2040]'
                    }`}
                    data-testid={`currencies-subtab-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {currencySubTab === 'list' ? <CurrenciesTab /> : <FxRatesCard />}
            </div>
          )}

          {activeTab === 'client-errors' && isSuperAdmin && (
            <ClientErrorsTab />
          )}

          {/* ═══════════ AUDIT LOG ═════════════════════════════ */}
          {activeTab === 'audit-log' && isSuperAdmin && (
            <div className="space-y-4">
              <QuoteGateAcksCard />
              <AuditLogTab />
            </div>
          )}

          {/* ═══════════ PERMISSIONS ════════════════════════════ */}
          {activeTab === 'permissions' && (
            <div className="space-y-4" data-testid="admin-permissions">
              <h2 className="text-base font-semibold text-[#0F2040]">Role-Based Permissions</h2>
              <div className="grid gap-4">
                {Object.entries(PERMISSION_MATRIX).map(([role, info]) => (
                  <Card key={role} data-testid={`permission-card-${role}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={`bg-${info.color}-100 text-${info.color}-700`}>{info.label}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1.5">
                        {info.permissions.map((perm) => (
                          <li key={perm} className="flex items-start gap-2 text-xs text-slate-600">
                            <div className={`w-1.5 h-1.5 rounded-full bg-${info.color}-400 mt-1 shrink-0`} />
                            {perm}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* ═══════════ ESTIMATE HISTORY ═══════════════════════ */}
          {activeTab === 'estimates' && (
            <div className="space-y-4" data-testid="admin-estimates">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[#0F2040]">All Estimate History</h2>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={estSearch} onChange={e => setEstSearch(e.target.value)} placeholder="Search estimates..." className="pl-8 text-xs h-8 w-56" data-testid="est-search" />
                </div>
              </div>

              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-2 py-2 text-xs font-medium text-slate-500 w-[28%]">Estimate</th>
                        <th className="text-left px-2 py-2 text-xs font-medium text-slate-500 w-[18%]">Created By</th>
                        <th className="text-left px-2 py-2 text-xs font-medium text-slate-500 w-[10%]">Type</th>
                        <th className="text-right px-2 py-2 text-xs font-medium text-slate-500 w-[10%]">Effort</th>
                        <th className="text-right px-2 py-2 text-xs font-medium text-slate-500 w-[10%]">Cost</th>
                        <th className="text-right px-2 py-2 text-xs font-medium text-slate-500 w-[10%]">Date</th>
                        <th className="text-center px-2 py-2 text-xs font-medium text-slate-500 w-[14%]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEstimates.map((est) => (
                        <tr key={est.id || `${est.estimate_name}-${est.created_at}`} className="border-b hover:bg-slate-50">
                          <td className="px-2 py-2">
                            <p className="font-medium text-[#0F2040] text-xs truncate">{est.estimate_name}</p>
                            <Badge variant="outline" className="text-[10px]">{est.results?.complexity_label || est.complexity}</Badge>
                          </td>
                          <td className="px-2 py-2 text-slate-600 text-xs">
                            <p className="truncate">{est.user_name}</p>
                            <p className="text-slate-400 truncate">{est.user_email}</p>
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-500">{est.implementation_type?.replace(/_/g, ' ')}</td>
                          <td className="px-2 py-2 text-right font-semibold text-[#0F2040] text-xs">{est.results?.total_effort?.toLocaleString() || 0}h</td>
                          <td className="px-2 py-2 text-right font-semibold text-[#4ABFCF] text-xs">${est.results?.total_cost?.toLocaleString() || 0}</td>
                          <td className="px-2 py-2 text-right text-xs text-slate-400">{est.created_at ? new Date(est.created_at).toLocaleDateString() : ''}</td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="outline" size="sm" className="h-6 w-6 p-0"
                                onClick={() => navigate(`/estimator?load=${est.id}`)}
                                data-testid={`admin-view-${est.id}`}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-[#4ABFCF] hover:text-[#3AAAB9] hover:border-[#4ABFCF]/40"
                                onClick={() => navigate(`/estimator/quote/${est.id}`)}
                                data-testid={`admin-bc-${est.id}`}>
                                <Briefcase className="w-3 h-3" />
                              </Button>
                              <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:border-red-300"
                                onClick={async () => {
                                  if (!window.confirm('Delete this estimate?')) return;
                                  try {
                                    await axios.delete(`${API_URL}/api/estimates/${est.id}`, { withCredentials: true });
                                    toast.success('Estimate deleted');
                                    fetchAll();
                                  } catch { toast.error('Failed to delete'); }
                                }}
                                data-testid={`admin-delete-${est.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredEstimates.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No estimates found.</p>}
                  {/* Estimate Pagination */}
                  {Math.ceil(estTotal / ADMIN_PAGE_SIZE) > 1 && (
                    <div className="flex items-center justify-center gap-3 pt-4" data-testid="est-pagination">
                      <Button variant="outline" size="sm" disabled={estPage <= 1} onClick={() => { const p = estPage - 1; setEstPage(p); fetchEstimates(p); }} data-testid="est-page-prev">Prev</Button>
                      <span className="text-xs text-slate-500">Page {estPage} of {Math.ceil(estTotal / ADMIN_PAGE_SIZE)}</span>
                      <Button variant="outline" size="sm" disabled={estPage >= Math.ceil(estTotal / ADMIN_PAGE_SIZE)} onClick={() => { const p = estPage + 1; setEstPage(p); fetchEstimates(p); }} data-testid="est-page-next">Next</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ═══════════ DASHBOARDS ═════════════════════════════ */}
          {activeTab === 'dashboards' && (
            <div className="space-y-4" data-testid="admin-dashboards">
              <h2 className="text-base font-semibold text-[#0F2040]">Dashboards</h2>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Estimate Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Total</p>
                        <p className="text-xl font-bold text-[#0F2040]">{estimates.length}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Avg Effort</p>
                        <p className="text-xl font-bold text-[#0F2040]">
                          {estimates.length ? Math.round(estimates.reduce((s, e) => s + (e.results?.total_effort || 0), 0) / estimates.length) : 0}h
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Avg Cost</p>
                        <p className="text-xl font-bold text-[#4ABFCF]">
                          ${estimates.length ? Math.round(estimates.reduce((s, e) => s + (e.results?.total_cost || 0), 0) / estimates.length).toLocaleString() : 0}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">By Implementation Type</p>
                      {Object.entries(estimates.reduce((acc, e) => { const t = e.implementation_type?.replace(/_/g, ' ') || 'Unknown'; acc[t] = (acc[t] || 0) + 1; return acc; }, {})).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between text-xs py-1">
                          <span className="text-slate-600">{type}</span>
                          <Badge variant="outline" className="text-[10px]">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Comparisons</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Orgs</p>
                        <p className="text-xl font-bold text-[#0F2040]">{orgs.length}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Active Users</p>
                        <p className="text-xl font-bold text-[#0F2040]">{users.length}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Est/User</p>
                        <p className="text-xl font-bold text-[#0F2040]">
                          {users.length ? (estimates.length / users.length).toFixed(1) : 0}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">By Complexity</p>
                      {Object.entries(estimates.reduce((acc, e) => { const c = e.results?.complexity_label || e.complexity || 'Unknown'; acc[c] = (acc[c] || 0) + 1; return acc; }, {})).map(([level, count]) => (
                        <div key={level} className="flex items-center justify-between text-xs py-1">
                          <span className="text-slate-600">{level}</span>
                          <Badge variant="outline" className="text-[10px]">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* ═══════════ REPORTS ════════════════════════════════ */}
          {activeTab === 'reports' && (
            <div className="space-y-4" data-testid="admin-reports">
              <h2 className="text-base font-semibold text-[#0F2040]">Reports</h2>
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-[#0F2040] mb-1">Report Generation</h3>
                  <p className="text-sm text-slate-500">Coming soon. Generate cross-estimate summary reports, utilization analysis, and forecasting reports.</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ═══════════ SETTINGS ═══════════════════════════════ */}
          {activeTab === 'settings' && (
            <div className="space-y-4" data-testid="admin-settings">
              <h2 className="text-base font-semibold text-[#0F2040]">Settings</h2>
              <SettingsSection title="General" testId="settings-section-general" defaultOpen>
                <div className="grid gap-4">
                  <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/settings')}>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#0F2040]/5 flex items-center justify-center"><Settings className="w-5 h-5 text-[#0F2040]" /></div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm text-[#0F2040]">Calculator Settings</h3>
                        <p className="text-xs text-slate-500">Rate cards, implementation types, modifier ranges, component configuration, and volume metrics.</p>
                      </div>
                      <ChevronUp className="w-4 h-4 text-slate-400 rotate-90" />
                    </CardContent>
                  </Card>
                  <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/planning-config')} data-testid="planning-config-card">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-violet-600" /></div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm text-[#0F2040]">Planning Configuration</h3>
                        <p className="text-xs text-slate-500">Baseline hours per role, phase duration presets, timing rules, complexity and implementation type multipliers.</p>
                      </div>
                      <ChevronUp className="w-4 h-4 text-slate-400 rotate-90" />
                    </CardContent>
                  </Card>
                </div>
              </SettingsSection>
              <SettingsSection title="Pricing" testId="settings-section-pricing">
                <div className="space-y-4">
                  <RegionalRateCardsCard />
                  <CostRateCardCard />
                </div>
              </SettingsSection>
              <SettingsSection title="Risk Badges" testId="settings-section-risk">
                <RiskSettingsCard />
              </SettingsSection>
              {isSuperAdmin && (
                <SettingsSection title="Integrations" testId="settings-section-integrations">
                  <GoogleDriveSettingsCard />
                </SettingsSection>
              )}
              {isSuperAdmin && (
                <SettingsSection title="Housekeeping" testId="settings-section-housekeeping">
                  <AutoArchiveSettingsCard />
                </SettingsSection>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ ORG FORM DIALOG ═══════════════════════════ */}
      <Dialog open={showOrgForm} onOpenChange={setShowOrgForm}>
        <DialogContent className="max-w-2xl" data-testid="org-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingOrg ? 'Edit Organization' : 'New Organization'}</DialogTitle>
          </DialogHeader>
          <OrgFormInline org={editingOrg} lookups={lookups} onSave={saveOrg} onCancel={() => setShowOrgForm(false)} saving={savingOrg} />
        </DialogContent>
      </Dialog>

      {/* ═══════════ USER FORM DIALOG ══════════════════════════ */}
      <Dialog open={showUserForm} onOpenChange={(open) => { setShowUserForm(open); if (!open) setOrgForNewUser(null); }}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User' : orgForNewUser ? `Add User to ${orgForNewUser.name}` : 'Create User'}
            </DialogTitle>
          </DialogHeader>
          <UserFormInline user={editingUser} orgs={orgs} onSave={saveUser} onCancel={() => { setShowUserForm(false); setOrgForNewUser(null); }} saving={savingUser} canAssignAllRoles={canAssignAllRoles} isSuperAdmin={isSuperAdmin} presetOrg={orgForNewUser} />
        </DialogContent>
      </Dialog>

      {/* Invite Email Dialog */}
      <InviteEmailDialog
        open={!!inviteData}
        onClose={() => { setInviteData(null); fetchAll(); }}
        userData={inviteData}
      />

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!userToDelete} onOpenChange={(open) => { if (!open) { setUserToDelete(null); setSaDeleteConfirmed(false); } }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          {userToDelete && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Are you sure you want to permanently delete <strong>{userToDelete.name}</strong> ({userToDelete.email})?
              </p>
              {userToDelete.role === 'super_admin' && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2" data-testid="delete-super-admin-warning">
                  <Crown className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-900">
                    <strong>{userToDelete.name}</strong> is a <strong>Super Admin</strong>. Deleting this account
                    removes a top-level administrator. This is a rare, high-impact action.
                  </p>
                </div>
              )}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-red-800">This action cannot be undone:</p>
                <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                  <li>The user account will be permanently removed</li>
                  <li>Login credentials will be invalidated</li>
                  {userToDelete.estimateCount > 0 && (
                    <li><strong>{userToDelete.estimateCount} estimate(s)</strong> will be reassigned to your account</li>
                  )}
                  <li>Saved scenarios and notification history will be deleted</li>
                </ul>
              </div>
              {userToDelete.role === 'super_admin' && (
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="delete-super-admin-confirm-label">
                  <Checkbox
                    checked={saDeleteConfirmed}
                    onCheckedChange={(v) => setSaDeleteConfirmed(!!v)}
                    data-testid="delete-super-admin-confirm-checkbox"
                  />
                  I understand I'm permanently deleting a Super Admin account.
                </label>
              )}
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => { setUserToDelete(null); setSaDeleteConfirmed(false); }} disabled={deletingUser} data-testid="cancel-delete-user-btn">
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDeleteUser}
                  disabled={deletingUser || (userToDelete.role === 'super_admin' && !saDeleteConfirmed)}
                  data-testid="confirm-delete-user-btn"
                >
                  {deletingUser ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Deleting...</> : 'Delete User'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Super Admin role-change (demote) Confirmation Dialog */}
      <Dialog open={!!pendingDemote} onOpenChange={(open) => { if (!open) setPendingDemote(null); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="demote-super-admin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Crown className="w-5 h-5" />
              Change Super Admin role
            </DialogTitle>
          </DialogHeader>
          {pendingDemote && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                You're about to change <strong>{editingUser?.name}</strong>'s role from
                {' '}<strong>Super Admin</strong> to <strong>{(ROLE_BADGES[pendingDemote.role] || {}).label || pendingDemote.role}</strong>.
              </p>
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                <p className="text-xs text-amber-900">
                  This removes their top-level administrative access, including the ability to manage other
                  administrators. They'll keep only the permissions of the new role.
                </p>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setPendingDemote(null)} disabled={savingUser} data-testid="cancel-demote-btn">
                  Cancel
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => { const d = pendingDemote; setPendingDemote(null); saveUser(d, true); }}
                  disabled={savingUser}
                  data-testid="confirm-demote-btn"
                >
                  {savingUser ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</> : 'Change role'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BuildVersionFooter />
    </div>
  );
}

